
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, Calendar, Check, Clock, ArrowRight, AlertCircle, FileText, CreditCard, Users } from 'lucide-react';
import LoadingScreen from '@/components/assessment/LoadingScreen';
import { handleAssessmentAccess } from '@/utils/assessment/assessmentAccess';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Type definition for Orders with string type for package_type
interface Order {
  id: string;
  package_type: string;
  amount: number;
  created_at: string;
  status: string;
  payment_completed_at: string | null;
  razorpay_payment_id: string | null;
  has_responses: boolean;
  has_results: boolean;
}

// Type definition for Bookings
interface Booking {
  id: string;
  partner_name: string;
  session_time: string;
  paid_amount: number;
  razorpay_payment_id: string | null;
  created_at: string;
  payment_status: string;
}

const MyOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useCentralizedAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!isAuthenticated) {
          console.log('Waiting for authentication state...');
          return;
        }

        if (!user?.id) {
          console.error('No user ID available');
          setError('Unable to verify your account. Please try logging in again.');
          setLoading(false);
          return;
        }

        console.log('Fetching orders for user:', user.id);
        setLoading(true);
        setError(null);
        
        // Fetch assessment orders
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'completed')
          .order('created_at', { ascending: false });

        if (orderError) {
          console.error('Error fetching orders:', orderError);
          setError('Failed to fetch your orders. Please try again later.');
          setLoading(false);
          return;
        }

        // Process orders data
        if (orderData && orderData.length > 0) {
          const orderIds = orderData.map(order => order.id);
          
          const { data: responseData, error: responseError } = await supabase
            .from('user_responses')
            .select('order_id')
            .in('order_id', orderIds)
            .eq('user_id', user.id);

          if (responseError) {
            console.error('Error checking responses:', responseError);
          }

          const { data: resultData, error: resultError } = await supabase
            .from('assessment_results')
            .select('order_id')
            .in('order_id', orderIds)
            .eq('user_id', user.id);

          if (resultError) {
            console.error('Error checking results:', resultError);
          }

          const orderIdsWithResponses = new Set(
            responseData?.map(response => response.order_id) || []
          );
          
          const orderIdsWithResults = new Set(
            resultData?.map(result => result.order_id) || []
          );

          const processedOrders = orderData.map(order => ({
            ...order,
            has_responses: orderIdsWithResponses.has(order.id),
            has_results: orderIdsWithResults.has(order.id)
          })) as Order[];

          console.log(`Found ${processedOrders.length} orders for user`);
          setOrders(processedOrders);
        } else {
          setOrders([]);
        }

        // Fetch counselor bookings
        console.log('Fetching bookings with profile_id:', user.id);
        
        // Updated query: Fetch completed bookings instead of paid
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select(`
            id,
            created_at,
            session_time,
            paid_amount,
            razorpay_payment_id,
            payment_status,
            partners(name)
          `)
          .eq('profile_id', user.id)
          .eq('payment_status', 'completed') // Changed from 'paid' to 'completed'
          .order('created_at', { ascending: false });

        if (bookingError) {
          console.error('Error fetching bookings:', bookingError);
          toast.error('Failed to load your counseling sessions');
        } else if (bookingData) {
          console.log('Booking data received:', bookingData);
          
          // Debug the raw data structure
          console.log('Raw booking data structure:', JSON.stringify(bookingData, null, 2));
          
          const processedBookings = bookingData.map(booking => {
            // Log individual booking for debugging
            console.log('Processing booking:', booking);
            
            return {
              id: booking.id,
              partner_name: booking.partners?.name || 'Unknown Counselor',
              session_time: booking.session_time,
              paid_amount: booking.paid_amount,
              razorpay_payment_id: booking.razorpay_payment_id,
              created_at: booking.created_at,
              payment_status: booking.payment_status
            };
          });
          
          setBookings(processedBookings);
          console.log(`Found ${processedBookings.length} bookings for user`);
        } else {
          console.log('No booking data found');
          setBookings([]);
        }
      } catch (error) {
        console.error('Error in fetchOrders:', error);
        setError('An error occurred while fetching your orders. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user, isAuthenticated, navigate]);

  const handleStartAssessment = async (orderId: string) => {
    await handleAssessmentAccess(navigate, user?.id, orderId);
  };

  const handleViewResults = (orderId: string) => {
    navigate(`/results?order=${orderId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPackageLabel = (packageType: string) => {
    return packageType === 'gold' ? 'Gold Package' : 'Silver Package';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <LoadingScreen message="Loading your orders..." />
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center p-6">
          <Card className="max-w-md w-full shadow-md">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center gap-4">
                <AlertCircle className="h-12 w-12 text-red-500" />
                <h2 className="text-xl font-semibold">Error Loading Orders</h2>
                <p className="text-gray-600">{error}</p>
                <Button 
                  onClick={() => navigate('/')}
                  className="mt-2"
                >
                  Return to Home
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const hasNoContent = orders.length === 0 && bookings.length === 0;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">My Orders</h1>
          
          {hasNoContent ? (
            <Card className="shadow-md">
              <CardContent className="p-6 text-center">
                <div className="py-10">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">No Orders Found</h2>
                  <p className="text-gray-500 mb-6">
                    You haven't made any purchases yet. Explore our packages to get started with your career assessment.
                  </p>
                  <Button 
                    onClick={() => navigate('/')}
                    className="bg-brand-blue hover:bg-brand-blue/90"
                  >
                    View Packages
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Tabs defaultValue="assessments" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="assessments" className="flex items-center">
                  <Package className="h-4 w-4 mr-2" /> Assessments
                </TabsTrigger>
                <TabsTrigger value="counseling" className="flex items-center">
                  <Users className="h-4 w-4 mr-2" /> Counseling Sessions
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="assessments" className="space-y-6">
                {orders.length === 0 ? (
                  <Card className="shadow-md">
                    <CardContent className="p-6 text-center">
                      <div className="py-8">
                        <Package className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                        <h2 className="text-lg font-semibold text-gray-700 mb-2">No Assessment Orders Found</h2>
                        <p className="text-gray-500 mb-4">
                          You haven't purchased any assessment packages yet.
                        </p>
                        <Button 
                          onClick={() => navigate('/')}
                          className="bg-brand-blue hover:bg-brand-blue/90"
                        >
                          Explore Packages
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {orders.map((order) => (
                      <Card key={order.id} className="shadow-md">
                        <CardHeader className="bg-gray-50 border-b">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Package className="h-5 w-5 text-brand-blue" />
                              {getPackageLabel(order.package_type)}
                            </CardTitle>
                            <Badge variant={order.has_results ? "secondary" : order.has_responses ? "outline" : "default"} 
                              className={`
                                ${order.has_results ? 'bg-green-100 text-green-800' : 
                                  order.has_responses ? 'bg-amber-100 text-amber-800' : 
                                  'bg-gray-100 text-gray-800'}
                              `}>
                              {order.has_results ? (
                                <><Check className="h-3 w-3 mr-1" /> Results Available</>
                              ) : order.has_responses ? (
                                <><Clock className="h-3 w-3 mr-1" /> Processing Results</>
                              ) : (
                                <><Clock className="h-3 w-3 mr-1" /> Assessment Pending</>
                              )}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-1">
                              <p className="text-sm text-gray-500">Order ID</p>
                              <p className="font-medium text-sm break-all">{order.id}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-gray-500">Transaction ID</p>
                              <p className="font-medium text-sm break-all">{order.razorpay_payment_id || 'N/A'}</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-1">
                              <p className="text-sm text-gray-500">Order Date</p>
                              <p className="font-medium flex items-center">
                                <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                {formatDate(order.created_at)}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-gray-500">Amount Paid</p>
                              <p className="font-medium">₹{order.amount}</p>
                            </div>
                          </div>
                          
                          <div className="mt-6 flex justify-end">
                            {order.has_results ? (
                              <Button 
                                onClick={() => handleViewResults(order.id)}
                                className={`px-4 py-2 ${order.package_type === 'gold' 
                                  ? 'bg-brand-yellow text-brand-blue hover:bg-amber-400' 
                                  : 'bg-brand-blue hover:bg-brand-blue/90'}`}
                              >
                                View Results <FileText className="ml-2 h-4 w-4" />
                              </Button>
                            ) : !order.has_responses ? (
                              <Button 
                                onClick={() => handleStartAssessment(order.id)}
                                className={`px-4 py-2 ${order.package_type === 'gold' 
                                  ? 'bg-brand-yellow text-brand-blue hover:bg-amber-400' 
                                  : 'bg-brand-blue hover:bg-brand-blue/90'}`}
                              >
                                Start Assessment <ArrowRight className="ml-2 h-4 w-4" />
                              </Button>
                            ) : (
                              <Button 
                                disabled
                                className="px-4 py-2 bg-gray-200 text-gray-500 cursor-not-allowed"
                              >
                                Processing Results <Clock className="ml-2 h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="counseling" className="space-y-6">
                {bookings.length === 0 ? (
                  <Card className="shadow-md">
                    <CardContent className="p-6 text-center">
                      <div className="py-8">
                        <Users className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                        <h2 className="text-lg font-semibold text-gray-700 mb-2">No Counseling Sessions Found</h2>
                        <p className="text-gray-500 mb-4">
                          You haven't booked any counseling sessions yet.
                        </p>
                        <Button 
                          onClick={() => navigate('/career-counselors')}
                          className="bg-brand-blue hover:bg-brand-blue/90"
                        >
                          Find Counselors
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {bookings.map((booking) => (
                      <Card key={booking.id} className="shadow-md">
                        <CardHeader className="bg-gray-50 border-b">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Users className="h-5 w-5 text-brand-blue" />
                              Career Counseling Session
                            </CardTitle>
                            <Badge 
                              variant="secondary"
                              className="bg-green-100 text-green-800"
                            >
                              <Check className="h-3 w-3 mr-1" /> Booked
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-1">
                              <p className="text-sm text-gray-500">Booking ID</p>
                              <p className="font-medium text-sm break-all">{booking.id}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-gray-500">Transaction ID</p>
                              <p className="font-medium text-sm break-all">{booking.razorpay_payment_id || 'N/A'}</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-1">
                              <p className="text-sm text-gray-500">Counselor</p>
                              <p className="font-medium">{booking.partner_name}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-gray-500">Amount Paid</p>
                              <p className="font-medium">₹{booking.paid_amount}</p>
                            </div>
                          </div>
                          
                          <div className="space-y-1 mb-4">
                            <p className="text-sm text-gray-500">Session Date & Time</p>
                            <p className="font-medium flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                              {formatDate(booking.session_time)} at {formatTime(booking.session_time)}
                            </p>
                          </div>
                          
                          <div className="mt-4 border-t pt-4">
                            <p className="text-sm text-gray-600">
                              Your counselor will reach out with session details via email.
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default MyOrders;
